import type { UnitOfMeasure, UnitOfMeasureCreate, UnitOfMeasureItemRef } from '@erp/shared';
import { Ban, Boxes, Package, Ruler, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
} from '../../components/shadcn/index.js';
import {
  Badge,
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { TranslationEditor } from '../cms/TranslationEditor.js';

/**
 * Units-of-measure master (the old POS `d_unit`). Simple per-tenant list —
 * name + sort + active — that the item form's base unit and pack/carton units
 * pick from. Reuses the items.* permissions.
 */
export function UnitsPage(): JSX.Element {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('items.write');
  const [units, setUnits] = useState<UnitOfMeasure[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<UnitOfMeasure | null>(null);
  // Which unit's "Related products" dialog is open (null = closed).
  const [productsFor, setProductsFor] = useState<UnitOfMeasure | null>(null);

  const reload = async (): Promise<void> => {
    const r = await api().get<{ items: UnitOfMeasure[] }>('/units-of-measure');
    setUnits(r.data.items);
  };
  useEffect(() => { void reload(); }, []);

  const onDeactivate = async (u: UnitOfMeasure): Promise<void> => {
    if (!(await confirmDialog({ message: t('units.actions.confirmDeactivate', { name: u.name, defaultValue: `Deactivate ${u.name}?` }), tone: 'danger' }))) return;
    try { await api().delete(`/units-of-measure/${u.id}`); await reload(); } catch { toast.error(tc('errors:server')); }
  };
  const onReactivate = async (u: UnitOfMeasure): Promise<void> => {
    try { await api().patch(`/units-of-measure/${u.id}`, { isActive: true }); await reload(); } catch { toast.error(tc('errors:server')); }
  };

  if (!units) {
    return <Card className="p-6"><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-32 w-full" /></Card>;
  }

  const columns: DataTableColumn<UnitOfMeasure>[] = [
    {
      id: 'name', header: t('units.columns.name', { defaultValue: 'Unit' }),
      sortValue: (u) => u.name, searchValue: (u) => u.name,
      cell: (u) => <span className="font-semibold">{u.name}</span>,
    },
    {
      id: 'products', header: t('units.columns.products', { defaultValue: 'Related products' }), align: 'end',
      sortValue: (u) => u.productCount ?? 0,
      cell: (u) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setProductsFor(u); }}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 font-mono tabular-nums text-fg-muted transition-colors hover:bg-bg-subtle hover:text-brand focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:outline-none"
          title={t('units.products.view', { defaultValue: 'View related products' })}
        >
          <Package className="h-3.5 w-3.5" aria-hidden />
          {u.productCount ?? 0}
        </button>
      ),
    },
    {
      id: 'sort', header: t('units.columns.sort', { defaultValue: 'Sort' }), align: 'end',
      sortValue: (u) => u.sort, cellClassName: 'font-mono tabular-nums text-fg-muted', cell: (u) => u.sort,
    },
    {
      id: 'status', header: t('units.columns.status', { defaultValue: 'Status' }),
      sortValue: (u) => (u.isActive ? 1 : 0),
      cell: (u) => (
        <Badge tone={u.isActive ? 'success' : 'neutral'}>
          {t(`status.${u.isActive ? 'active' : 'inactive'}`, { defaultValue: u.isActive ? 'Active' : 'Inactive' })}
        </Badge>
      ),
    },
    ...(canWrite
      ? [{
          id: 'actions', header: '', align: 'end' as const, headClassName: 'w-28',
          cell: (u: UnitOfMeasure) => (
            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setEditing(u); window.scrollTo({ top: 0, behavior: 'smooth' }); }} aria-label={`${tc('actions.edit')} ${u.name}`} title={tc('actions.edit')}>
                <Ruler className="h-4 w-4" aria-hidden />
              </Button>
              {u.isActive ? (
                <Button variant="ghost" size="sm" onClick={() => void onDeactivate(u)} aria-label={`${t('units.actions.deactivate', { defaultValue: 'Deactivate' })} ${u.name}`} title={t('units.actions.deactivate', { defaultValue: 'Deactivate' })}>
                  <Ban className="h-4 w-4" aria-hidden />
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => void onReactivate(u)} aria-label={`${t('units.actions.reactivate', { defaultValue: 'Reactivate' })} ${u.name}`} title={t('units.actions.reactivate', { defaultValue: 'Reactivate' })}>
                  <RotateCcw className="h-4 w-4" aria-hidden />
                </Button>
              )}
            </div>
          ),
        }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('units.title', { defaultValue: 'Units of measure' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t('units.description', { defaultValue: 'The units items are counted in (piece, pack, carton…). The item form picks its base and pack units from this list.' })}
          </p>
        </div>
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => setShowCreate(true)}>{t('units.create.title', { defaultValue: 'Add unit' })}</Button>
        )}
      </header>

      {showCreate && <UnitForm onCancel={() => setShowCreate(false)} onSaved={async () => { setShowCreate(false); await reload(); }} />}
      {editing && <UnitForm initial={editing} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); await reload(); }} />}

      {units.length === 0 && !showCreate ? (
        <EmptyState
          icon={<Ruler className="h-8 w-8" aria-hidden />}
          title={t('units.empty', { defaultValue: 'No units yet' })}
          description={t('units.emptyDescription', { defaultValue: 'Add the units you sell in — piece, pack, carton, dozen…' })}
          action={canWrite ? <Button size="sm" onClick={() => setShowCreate(true)}>{t('units.create.title', { defaultValue: 'Add unit' })}</Button> : undefined}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={columns}
            data={units}
            rowKey={(u) => u.id}
            rowClassName={(u) => (u.isActive ? undefined : 'opacity-60')}
            onRowClick={(u) => setProductsFor(u)}
            searchPlaceholder={t('units.searchPlaceholder', { defaultValue: 'Search unit…' })}
          />
        </Card>
      )}

      <UnitProductsDialog unit={productsFor} onClose={() => setProductsFor(null)} />
    </div>
  );
}

/**
 * Click-to-show list of the products that use a given unit as their base unit.
 * Fetches lazily when opened and links each product to its detail page.
 */
function UnitProductsDialog({ unit, onClose }: { unit: UnitOfMeasure | null; onClose: () => void }): JSX.Element {
  const { t } = useTranslation('inventory');
  const [items, setItems] = useState<UnitOfMeasureItemRef[] | null>(null);

  useEffect(() => {
    if (!unit) { setItems(null); return; }
    let active = true;
    setItems(null);
    void (async () => {
      try {
        const r = await api().get<{ items: UnitOfMeasureItemRef[] }>(`/units-of-measure/${unit.id}/items`);
        if (active) setItems(r.data.items);
      } catch {
        if (active) setItems([]);
      }
    })();
    return () => { active = false; };
  }, [unit]);

  return (
    <Dialog open={!!unit} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[80vh] max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pe-6">
            <Boxes className="h-5 w-5 text-fg-muted" aria-hidden />
            {t('units.products.title', { name: unit?.name, defaultValue: 'Products using {{name}}' })}
          </DialogTitle>
        </DialogHeader>
        <div className="-mx-2 max-h-[60vh] overflow-y-auto">
          {items === null ? (
            <div className="space-y-2 px-2 py-1">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : items.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-fg-muted">
              {t('units.products.empty', { defaultValue: 'No products use this unit yet.' })}
            </p>
          ) : (
            <ul className="flex flex-col">
              {items.map((it) => (
                <li key={it.id}>
                  <Link
                    to={`/inventory/items/${it.id}`}
                    onClick={onClose}
                    className="flex items-center justify-between gap-3 rounded-md px-2 py-2 transition-colors hover:bg-bg-subtle"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Package className="h-4 w-4 shrink-0 text-fg-muted" aria-hidden />
                      <span className={`truncate ${it.isActive ? 'text-fg' : 'text-fg-muted line-through'}`}>{it.name}</span>
                    </span>
                    <span className="shrink-0 font-mono text-xs text-fg-muted">{it.code}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UnitForm({ initial, onCancel, onSaved }: { initial?: UnitOfMeasure; onCancel: () => void; onSaved: () => void | Promise<void> }): JSX.Element {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const isEdit = !!initial;
  const [form, setForm] = useState<UnitOfMeasureCreate>(() => (initial ? { name: initial.name, sort: initial.sort } : { name: '', sort: 0 }));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isEdit && initial) await api().patch(`/units-of-measure/${initial.id}`, { name: form.name.trim(), sort: form.sort ?? 0 });
      else await api().post('/units-of-measure', { name: form.name.trim(), sort: form.sort ?? 0 });
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'conflict' ? t('units.errors.duplicate', { defaultValue: 'That unit name is already in use.' }) : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">
        {isEdit ? t('units.edit.title', { name: initial?.name, defaultValue: `Edit ${initial?.name}` }) : t('units.create.title', { defaultValue: 'Add unit' })}
      </h2>
      <form className="grid grid-cols-1 gap-4 sm:grid-cols-2" onSubmit={onSubmit} noValidate>
        <FormField id="uom-name" label={t('units.create.name', { defaultValue: 'Name' })} required>
          <Input id="uom-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus maxLength={100} />
        </FormField>
        <FormField id="uom-sort" label={t('units.create.sort', { defaultValue: 'Sort order' })}>
          <Input id="uom-sort" type="number" value={String(form.sort ?? 0)} onChange={(e) => setForm({ ...form, sort: Number(e.target.value) || 0 })} />
        </FormField>
        {isEdit && initial && (
          <div className="sm:col-span-2">
            {/* The translated name follows the unit BY NAME onto every product
                (base unit + pack/carton levels) on the store and the app. */}
            <TranslationEditor
              entityType="uom"
              entityId={initial.id}
              fields={[{ key: 'name', label: t('units.create.name', { defaultValue: 'Name' }) }]}
            />
          </div>
        )}
        {error && <p className="sm:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="sm:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{isEdit ? tc('actions.save') : t('units.create.submit', { defaultValue: 'Create unit' })}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
